import { createAsyncThunk } from "@reduxjs/toolkit";
import type { FieldConfiguration } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ReportField } from "../../../../../../api/types/svTypes.ts";
import { createTypedAsyncThunk } from "../utils/createTypedAsyncThunk.ts";
import { getSortField } from "../utils/getSortField.ts";
import { createPivotAction } from "../utils/storeUtils.ts";

const setRows = createAsyncThunk<void, ReportField[]>("pivotFields/setRows", (rows: ReportField[], { dispatch }) => {
  dispatch(createPivotAction("setRows", rows));
});

const addRow = createAsyncThunk<void, { field: ReportField; index: number }>(
  "pivotFields/addRow",
  (payload: { field: ReportField; index: number }, { dispatch }) => {
    dispatch(createPivotAction("addRow", payload));
  }
);

const removeRow = createTypedAsyncThunk<void, ReportField>(
  "pivotFields/removeRow",
  (field: ReportField, { dispatch, getState }) => {
    dispatch(createPivotAction("removeRow", field));
    const sortingField = getSortField(field, getState().fieldsState.pivotFieldsReducer.sorts);
    if (sortingField) {
      dispatch(createPivotAction("removeSorting", sortingField));
    }
  }
);

const moveRow = createAsyncThunk<void, { field: ReportField; newIndex: number }>(
  "pivotFields/moveRow",
  (payload: { field: ReportField; newIndex: number }, { dispatch }) => {
    dispatch(createPivotAction("moveRow", payload));
  }
);

const updateRow = createAsyncThunk<void, { field: ReportField; changes: Partial<ReportField> }>(
  "pivotFields/updateRow",
  (payload: { field: ReportField; changes: Partial<ReportField> }, { dispatch }) => {
    dispatch(createPivotAction("updateRow", payload));
  }
);

const updateRowConfig = createTypedAsyncThunk<void, { field: ReportField; changes: Partial<FieldConfiguration> }>(
  "pivotFields/updateRowConfig",
  (payload: { field: ReportField; changes: Partial<FieldConfiguration> }, { dispatch, getState }) => {
    dispatch(createPivotAction("updateRowConfig", payload));
    const sortingField = getSortField(payload.field, getState().fieldsState.pivotFieldsReducer.sorts);
    if (sortingField && payload.changes.customLabel !== payload.field.config?.customLabel) {
      dispatch(
        createPivotAction("updateSortingConfig", {
          field: sortingField,
          changes: { caption: payload.changes.customLabel },
        })
      );
    }
  }
);

// Column thunks
const setColumns = createAsyncThunk<void, ReportField[]>(
  "pivotFields/setColumns",
  (columns: ReportField[], { dispatch }) => {
    dispatch(createPivotAction("setColumns", columns));
  }
);

const addColumn = createAsyncThunk<void, { field: ReportField; index: number }>(
  "pivotFields/addColumn",
  (payload: { field: ReportField; index: number }, { dispatch }) => {
    dispatch(createPivotAction("addColumn", payload));
  }
);

const removeColumn = createTypedAsyncThunk<void, ReportField>(
  "pivotFields/removeColumn",
  (field: ReportField, { dispatch, getState }) => {
    dispatch(createPivotAction("removeColumn", field));
    const sortingField = getSortField(field, getState().fieldsState.pivotFieldsReducer.sorts);
    if (sortingField) {
      dispatch(createPivotAction("removeSorting", sortingField));
    }
  }
);

const moveColumn = createAsyncThunk<void, { field: ReportField; newIndex: number }>(
  "pivotFields/moveColumn",
  (payload: { field: ReportField; newIndex: number }, { dispatch }) => {
    dispatch(createPivotAction("moveColumn", payload));
  }
);

const updateColumnConfig = createTypedAsyncThunk<void, { field: ReportField; changes: Partial<FieldConfiguration> }>(
  "pivotFields/updateColumnConfig",
  (payload: { field: ReportField; changes: Partial<FieldConfiguration> }, { dispatch, getState }) => {
    dispatch(createPivotAction("updateColumnConfig", payload));
    const sortingField = getSortField(payload.field, getState().fieldsState.pivotFieldsReducer.sorts);
    if (sortingField && payload.changes.customLabel !== payload.field.config?.customLabel) {
      dispatch(
        createPivotAction("updateSortingConfig", {
          field: sortingField,
          changes: { caption: payload.changes.customLabel },
        })
      );
    }
  }
);

export const pivotFieldsStateThunks = {
  setRows,
  addRow,
  removeRow,
  moveRow,
  updateRow,
  updateRowConfig,
  setColumns,
  addColumn,
  removeColumn,
  moveColumn,
  updateColumnConfig,
};
